'use client'

import { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react'
import { Button, Cell, Dialog, Divider, InputTextArea, Radio, Text } from '@vinted/web-ui'
import { snakeCase } from 'lodash'

import { FeedbackOptionsModel } from '@marketplace-web/conversation-feedback/conversation-feedback-data'

import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

type Props = {
  isOpen: boolean
  options: Array<FeedbackOptionsModel>
  onClose: () => void
  onSubmit: (selectedOption: number | undefined, message: string) => void
}

const DenyHelpCenterFeedbackModal = ({ isOpen, options, onClose, onSubmit }: Props) => {
  const [selectedOption, setSelectedOption] = useState<number>()
  const [isInputVisible, setIsInputVisible] = useState(false)
  const [message, setMessage] = useState<string>('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const translate = useTranslate('help_center_feedback')

  useEffect(() => {
    const showInput = options.find(option => option.id === selectedOption && option.hasMessageInput)

    setIsInputVisible(!!showInput)
  }, [options, selectedOption])

  useEffect(() => {
    if (isInputVisible) inputRef.current?.focus()
  }, [isInputVisible])

  function handleModalClose() {
    setMessage('')
    setSelectedOption(undefined)
    onClose()
  }

  function sendProblemChoice() {
    onSubmit(selectedOption, message)
  }

  function handleTextAreaChange({ target }: ChangeEvent<HTMLTextAreaElement>) {
    setMessage(target.value)
  }

  function renderOptions({ id, body }: FeedbackOptionsModel) {
    const isSelected = selectedOption === id
    const name = snakeCase(body)

    const suffix = (
      <Radio
        name="deny_feedback_option"
        value={name}
        checked={isSelected}
        onChange={() => undefined}
        aria={{
          'aria-labelledby': `radio-label-${name}`,
        }}
      />
    )

    const handleProblemOptionClick = () => {
      setMessage('')
      setSelectedOption(id)
    }

    return (
      <Fragment key={id}>
        <Cell suffix={suffix} title={body} onClick={handleProblemOptionClick} />
        <Divider />
      </Fragment>
    )
  }

  return (
    <Dialog show={isOpen} hasScrollableContent>
      <div className="u-position-relative u-fill-width">
        <ScrollableArea>
          <div className="feedback-modal__title">
            <Text as="h1" type="heading" text={translate('feedback_deny_resolved.title')} />
          </div>
          {options.map(renderOptions)}
          {isInputVisible && (
            <InputTextArea
              ref={inputRef}
              maxRows={5}
              placeholder={translate('feedback_deny_resolved.input_placeholder')}
              value={message}
              name="auto_reply_deny_comment"
              onChange={handleTextAreaChange}
            />
          )}
          <div className="feedback-modal__actions">
            <Button
              text={translate('actions.submit')}
              styling="filled"
              onClick={sendProblemChoice}
              disabled={!selectedOption}
            />
            <Button text={translate('actions.close')} styling="flat" onClick={handleModalClose} />
          </div>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default DenyHelpCenterFeedbackModal
